import { api } from 'boot/axios';
import apiConfig from 'src/apiConfig';
import { Cookies } from 'quasar';
import
 {LoginData,
  LogoutData,
  LoginResponse,
  LogoutResponse,
  TokenResponse,
  RefreshResponse,
  RegisterData,
  RegisterResponse,
  UserInfoResponse,
  UserInfo,}
  from '../types';

/**
 * class for making backend API calls related to authentication
 */
class AuthService {

  getRefreshToken() {
    return Cookies.get('refresh_token');
  }

  /**
   * Login a user
   * @param {String} username
   * @param {String} password
   * @returns {String} Access token
   */
  async login(loginData: LoginData): Promise<LoginResponse>  {
      try {
        const { data } = await api.post<TokenResponse>(apiConfig.auth.login, loginData, { withCredentials: true });
        return [null, data];
      } catch (error: any) {
        return [error]
      }
    }

  async register(userData: RegisterData, studentToken?: string): Promise<RegisterResponse>  {
      try {
        const payload = studentToken ? { ...userData, studentToken } : userData;
        const { status } = await api.post(apiConfig.auth.signUp, payload);
      return [null, { status }];
    } catch (error: any) {
      return [error];
    }
  }

  async googleLogin(query: any) {
    try {
      const response = await api.get(apiConfig.auth.googleLogin, {
        params: { ...query },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async wechatLogin(query: any) {
    try {
      const response = await api.get(apiConfig.auth.wechatLogin, {
        params: { ...query },
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Logout
   *
   * This API call removes the HttpOnly refresh token cookie to logout the current logged in user
   */
  async logout(): Promise<LogoutResponse> {
    try {
      const { data } = await api.post<LogoutData>(apiConfig.auth.logout);
      return [null, data];
    } catch (error: any) {
      return [error]
    }
  }

  /**
  * Refresh token
  */
  async refreshToken(): Promise<TokenResponse> {
    try {
      const { data } = await api.post<TokenResponse>(apiConfig.auth.refresh);
      return data;
    } catch (error) {
      throw error;
    }
  }

  async sendTimezone() {
    try {
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const response = await api.post(apiConfig.auth.sendTimezone, {
        timezone: timezone,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async setLanguage(language: string) {
    try {
      const response = await api.post(apiConfig.auth.setLanguage, {language});
      return response;
    } catch (error) {
      throw error;
    }
  }

  async readPetLetter() {
    try {
      const response = await api.post(apiConfig.auth.readPetLetter);
      return response;
    } catch (error) {
      throw error;
    }
  }

 async getUserInfo(): Promise<UserInfoResponse> {
  try {
    const { data } = await api.get<UserInfo>(apiConfig.auth.userInfo);
    return [null, data];
  } catch (error: any) {
    return [error]
  }
 }

 async updateUserInfo(updateData: Partial<UserInfo>): Promise<UserInfoResponse> {
  try {
    const { data } = await api.post<UserInfo>(apiConfig.auth.userInfo, updateData);
    return [null, data];
  } catch (error: any) {
    return [error];
  }
}

  async getUserEmailVerificationInfo(): Promise<{
    email: string | null;
    isVerified: boolean;
  }> {
    try {
      const response = await api.get(apiConfig.auth.emailVerificationCheck);
      return {
        email: response.data.email,
        isVerified: response.data.is_verified,
      };
    } catch (error) {
      throw error;
    }
  }

  async enrollStudent(userId: string, token: string) {
    try {
      const response = await api.post(apiConfig.auth.enroll, {
        user_id: userId,
        token: token,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async sendVerifyEmail () {
    try {
      const response = await api.post(apiConfig.auth.sendVerifyEmail, null);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async adminSendVerifyEmail (userId: string) {
    try {
      const response = await api.post(apiConfig.auth.adminSendVerifyEmail,
        {
        user_id: userId,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async sendResetEmail(email: string) {
    try {
      const response = await api.post(
        apiConfig.auth.resetPasswordRequest,
        {
          email: email,
        },
        { headers: {} }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async resetEmailVerify(token: string | string[]) {
    try {
      const response = await api.post(
        apiConfig.auth.resetPasswordVerify,
        {
          token: token,
        }
      );
      return response;
    } catch (error) {
      throw error;
    }
  }

  async resetSetPassword(token: string | string[], password: string) {
    try {
      const response = await api.post(apiConfig.auth.resetPassword, {
        token: token,
        password: password,
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async verify(token: string, agencyInviteToken?: string) {
    const data: { token: string; agency_invite_token?: string } = { token };
    if (agencyInviteToken) {
      data.agency_invite_token = agencyInviteToken;
    }
    try {
      const response = await api.post(apiConfig.auth.verifyEmail, data);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async setOnboarding() {
    try {
      const response = await api.post(apiConfig.auth.setOnboarding);
      return response;
    } catch (error) {
      throw error;
    }
  }

  async adminAddStudent(studentInfo: Record<string, any>, agency_id: string) {
    try {
      const response = await api.post(apiConfig.auth.adminAddStudent, {
        ...studentInfo,
      }, {
        params: { agency_id }
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async adminAddStudents(payload: Record<string, any>, agency_id: string) {
    try {
      const response = await api.post(apiConfig.auth.adminAddStudents, payload, {
        params: { agency_id }
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async adminAddTeacher(teacherInfo: Record<string, any>, agency_id: string) {
    try {
      const response = await api.post(apiConfig.auth.adminAddTeacher, {
        ...teacherInfo,
      }, {
        params: { agency_id }
      });
      return response;
    } catch (error) {
      throw error;
    }
  }

  async getBillingUsage() {
    try{
      const response = await api.get(apiConfig.auth.billingUsage)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getAcademyLogoFromDomainName(domain_name: string) {
    try{
      const response = await api.get(apiConfig.auth.getAcademyLogoFromDomain, {
        params: {domain_name}
      })
      return response.data;
    } catch (error) {
      throw error;
    }
  }
  async getEncryptedUserId() {
    try{
      const response = await api.get(apiConfig.auth.getEncryptedUserId)
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  async getTeacherStripeDetails() {
    try {
      const response = await api.get(apiConfig.auth.getTeacherStripeDetails);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
}

export default new AuthService();
